import React, { useState, useEffect, useCallback } from "react";
import { Check, Loader2, AlertCircle, Home, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";

import { API } from "@/config/api";
const API_URL = API.replace('/api', '');

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);

  const checkPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setStatus("error");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/stripe/status/${sessionId}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentDetails(data);

        if (data.payment_status === "paid") {
          setStatus("success");
          // Clear pending payment from localStorage
          localStorage.removeItem("pending_payment");
        } else if (data.status === "expired") {
          setStatus("error");
        } else {
          // Continue polling
          setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
        }
      } else {
        setStatus("error");
      }
    } catch (err) {
      console.error("Payment status check failed:", err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    checkPaymentStatus(sessionId, 0);
  }, [searchParams, checkPaymentStatus]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="w-full max-w-md text-center">
        {status === "checking" && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto bg-[#7a6200]/20 rounded-full flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#7a6200] animate-spin" />
            </div>
            <h1 className="font-bebas text-3xl text-white tracking-wider">VERIFYING PAYMENT</h1>
            <p className="text-[#A1A1AA] font-ubuntu">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-bebas text-3xl text-white tracking-wider">PAYMENT SUCCESSFUL!</h1>
            <p className="text-[#A1A1AA] font-ubuntu">Thank you for your purchase.</p>
            
            {paymentDetails && (
              <div className="bg-[#1A1A1A] border border-[#7a6200]/30 rounded-xl p-6 mt-6">
                <div className="space-y-3 text-left">
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Amount</span>
                    <span className="text-white font-medium">${(paymentDetails.amount_total / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Status</span>
                    <span className="text-green-500 font-medium">Completed</span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 mt-8">
              <Button
                onClick={() => navigate("/home")}
                variant="outline"
                className="flex-1 border-[#7a6200]/30 text-[#A1A1AA] hover:bg-[#7a6200]/10"
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
              <Button
                onClick={() => navigate("/profile")}
                className="flex-1 btn-gold"
              >
                <Calendar className="w-4 h-4 mr-2" />
                My Events
              </Button>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-bebas text-3xl text-white tracking-wider">PAYMENT ISSUE</h1>
            <p className="text-[#A1A1AA] font-ubuntu">
              We could not verify your payment. If you were charged, please contact support.
            </p>
            
            <Button
              onClick={() => navigate("/home")}
              className="btn-gold mt-8"
            >
              Return to Home
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
